import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './AccountPage.css';

export default function AccountPage() {
  const { user, setUser, logout } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');

  const avatarLetter = (user?.full_name?.[0] || user?.email?.[0] || '?').toUpperCase();
  const isLocalUser = user?.auth_provider === 'local';
  const hasPassword = user?.has_password;

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const { data } = await api.put('/auth/me', { full_name: fullName, phone: phone || null });
      setUser(data);
      setMessage('Profile updated!');
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setPasswordMsg('Passwords do not match'); return; }
    if (password.length < 8) { setPasswordMsg('Minimum 8 characters'); return; }
    if (hasPassword && !currentPassword) { setPasswordMsg('Enter your current password'); return; }
    setPasswordMsg('');
    try {
      await api.post('/auth/me/set-password', {
        current_password: hasPassword ? currentPassword : undefined,
        new_password: password,
        confirm_password: confirmPassword,
      });
      setPasswordMsg('Password updated!');
      setCurrentPassword('');
      setPassword('');
      setConfirmPassword('');
      // Refresh user so has_password reflects the new state
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (err) {
      setPasswordMsg(err.response?.data?.detail || 'Failed to update password');
    }
  };

  if (!user) return null;

  return (
    <div className="account-page page-content">
      <h1>My Account</h1>

      {/* Avatar + basic info */}
      <div className="account-card">
        <div className="account-avatar">
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="avatar" />
          ) : (
            <span>{avatarLetter}</span>
          )}
        </div>
        <div className="account-info">
          <h2>{user.full_name || 'User'}</h2>
          <p className="account-email">{user.email}</p>
          <p className="account-roles">
            {(user.roles || []).map(r => (
              <span key={r} className={`role-badge ${r}`}>{r}</span>
            ))}
          </p>
        </div>
      </div>

      {/* Edit profile */}
      <section className="account-section">
        <h3>Edit Profile</h3>
        <form onSubmit={handleUpdateProfile} className="account-form">
          <label>
            Full Name
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </label>
          <label>
            Email
            <input type="email" value={user.email} disabled />
          </label>
          <label>
            Phone number
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+20 10 xxxx xxxx"
            />
          </label>
          <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
          {message && <p className="account-msg">{message}</p>}
        </form>
      </section>

      {/* Set / change password */}
      <section className="account-section">
        <h3>{hasPassword ? 'Change Password' : 'Set Password'}</h3>
        {!isLocalUser && !hasPassword && (
          <p className="account-section-hint">
            You signed in with {user.auth_provider}. Optionally set a password to also log in with your email.
          </p>
        )}
        <form onSubmit={handleSetPassword} className="account-form">
          {hasPassword && (
            <label>
              Current Password
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </label>
          )}
          <label>
            New Password
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters" />
          </label>
          <label>
            Confirm Password
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          </label>
          <button type="submit">{hasPassword ? 'Change Password' : 'Set Password'}</button>
          {passwordMsg && <p className="account-msg">{passwordMsg}</p>}
        </form>
      </section>

      <button className="sign-out-btn" onClick={logout}>Sign Out</button>
    </div>
  );
}
